/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `ReassignChatModal` for the Caintra Connect frontend.
 * @module modals/ReassignChatModal/ReassignChatModal
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styles from "./ReassignChatModal.module.css";
import { CircularProgress, Backdrop } from "@mui/material";
import { ReactComponent as ActiveIcon } from "../../assets/icons/Chat/ActiveIcon.svg";
import { ReactComponent as SearchIcon } from "../../assets/icons/Chat/SearchIcon.svg";
import ConfirmationModal from "../ConfirmationModal";
import {
  getFilteredActiveUsersFromCompany,
  getUser,
} from "../../logic/user/index.js";
import { reassignChat } from "../../logic/chat/index.js";
import { getCompanyByRFC } from "../../logic/company/index.js";
import AssetImage from "../../components/AssetImage";

const ReassignChatModal = ({
  isOpen,
  onClose,
  onReassign,
  selectedChat,
  selectedUser: chatSelectedUser,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [companies, setCompanies] = useState({});
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const confirmationDialogRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchActiveUsers();
    }
  }, [isOpen]);

  const fetchCompaniesData = async (users) => {
    if (users && users.length > 0) {
      setIsLoadingCompanies(true);
      const companiesData = {};

      for (const user of users) {
        if (user.RFC && !companiesData[user.RFC]) {
          try {
            const companyData = await getCompanyByRFC(user.RFC, navigate);
            if (companyData) {
              companiesData[user.RFC] = companyData;
            }
          } catch (error) {
            logger.error(`Error fetching company data for RFC ${user.RFC}:`, {
              err: sanitizeError(error),
            });
          }
        }
      }

      setCompanies(companiesData);
      setIsLoadingCompanies(false);
    }
  };

  const fetchActiveUsers = async () => {
    setLoading(true);
    try {
      const currentUserId = localStorage.getItem("userId");

      if (!currentUserId) {
        logger.error("User ID not found in localStorage");
        setUsers([]);
        return;
      }

      const currentUser = await getUser(currentUserId, navigate);

      if (!currentUser || !currentUser.RFC) {
        logger.error("Current user data or RFC not found");
        setUsers([]);
        return;
      }

      const filteredActiveUsers = await getFilteredActiveUsersFromCompany(
        currentUser.RFC,
        currentUserId,
        navigate,
      );

      setUsers(filteredActiveUsers);
      await fetchCompaniesData(filteredActiveUsers);
    } catch (error) {
      logger.error("Error fetching active users:", {
        err: sanitizeError(error),
      });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter(
    (user) =>
      user.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.correoElectronico
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      user.nombreRol.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  const handleReassignChat = () => {
    if (!selectedUser || !selectedChat || !chatSelectedUser) {
      logger.error("Missing required data for chat reassignment");
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmReassignment = async () => {
    setIsSubmitting(true);
    try {
      const currentUserId = localStorage.getItem("userId");

      // Determine which user in the chat is being reassigned (the one that is not the current user)
      const userBeingReassignedId =
        selectedChat.userAId === currentUserId
          ? selectedChat.userBId
          : selectedChat.userAId;

      const result = await reassignChat(
        selectedChat.PK, // chatId
        currentUserId, // userReassigningId
        userBeingReassignedId, // userBeingReassignedId
        selectedUser._id, // selectedUserToReassignId
        navigate,
      );

      if (result.success) {
        await onReassign(selectedUser, result);
        onClose();
      } else {
        logger.error("Failed to reassign chat:", { data: result.message });
      }
    } catch (error) {
      logger.error("Error reassigning chat:", { err: sanitizeError(error) });
    } finally {
      setIsSubmitting(false);
      setShowConfirmation(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setShowConfirmation(false);
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      if (showConfirmation) {
        handleCancelConfirmation();
      } else {
        handleCancel();
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoadingCompanies}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
      <div className={styles.modalContainer}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {t("components.reassignChatModal.title")}
          </h2>
        </div>

        <div className={styles.modalContent}>
          <p className={styles.modalDescription}>
            {t("components.reassignChatModal.description")}
          </p>

          <div className={styles.searchContainer}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              placeholder={t("components.reassignChatModal.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.usersList}>
            {loading ? (
              <div className={styles.loadingUsers}>
                <CircularProgress size={20} color="inherit" />
              </div>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div
                  key={user._id}
                  className={`${styles.userItem} ${
                    selectedUser?._id === user._id ? styles.selectedUser : ""
                  }`}
                  onClick={() => handleUserSelect(user)}
                >
                  <div className={styles.userAvatarContainer}>
                    <div className={styles.avatarWrapper}>
                      {user.imagenDePerfil ? (
                        <AssetImage
                          src={user.imagenDePerfil}
                          alt={t("common.userProfileAlt", { name: user.nombre })}
                          className={styles.userProfileImage}
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div className={styles.defaultAvatar}>
                        {user.nombre.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    {user.nombreRol && (
                      <span className={styles.userRoleBadge}>
                        {user.nombreRol}
                      </span>
                    )}
                  </div>

                  <div className={styles.userInfoSection}>
                    <div className={styles.userNameContainer}>
                      <ActiveIcon className={styles.statusIcon} />
                      <div className={styles.userInfoContainer}>
                        <span className={styles.userNameText}>
                          {user.nombre}
                        </span>
                        <span className={styles.userCompanyName}>
                          {companies[user.RFC]?.nombreComercial ||
                            t("common.defaultCompanyName")}
                        </span>
                        {(user.numeroSocio || user.RFC) && (
                          <span className={styles.userCompanyDetails}>
                            {user.numeroSocio && (
                              <span>{user.numeroSocio}</span>
                            )}
                            {user.numeroSocio && user.RFC && (
                              <span className={styles.separator}>/</span>
                            )}
                            {user.RFC && <span>{user.RFC}</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.noUsersFound}>
                <p className={styles.noUsersText}>
                  {t("components.reassignChatModal.noUsersFound")}
                </p>
              </div>
            )}
          </div>

          <div className={styles.modalButtons}>
            <button
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {t("components.reassignChatModal.cancelButton")}
            </button>
            <button
              className={styles.reassignButton}
              onClick={handleReassignChat}
              disabled={!selectedUser || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} color="inherit" />
                  <span>{t("components.reassignChatModal.sending")}</span>
                </>
              ) : (
                <>{t("components.reassignChatModal.reassignButton")}</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <ConfirmationModal
          dialogRef={confirmationDialogRef}
          open={showConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmReassignment}
          title={t("components.reassignChatModal.confirmationTitle")}
          message={t("components.reassignChatModal.confirmationMessage", {
            user1Name:
              selectedChat && selectedUser
                ? (() => {
                    const currentUserId = localStorage.getItem("userId");
                    const userBeingReassignedId =
                      selectedChat.userAId === currentUserId
                        ? selectedChat.userBId
                        : selectedChat.userAId;
                    // Find the user being reassigned from the users list or use chatSelectedUser
                    const userBeingReassigned =
                      users.find(
                        (user) => user._id === userBeingReassignedId,
                      ) || chatSelectedUser;
                    return userBeingReassigned?.nombre;
                  })()
                : t("components.reassignChatModal.noUsersFound"),
            user2Name: selectedUser?.nombre,
          })}
          confirmButtonText={t("components.reassignChatModal.confirmButton")}
          cancelButtonText={t(
            "components.reassignChatModal.cancelConfirmButton",
          )}
          isLoading={isSubmitting}
          loadingButtonText={t(
            "components.reassignChatModal.loadingButtonText",
          )}
        />
      )}
    </div>
  );
};

export default ReassignChatModal;
